import React, { useEffect, useState } from 'react';

import { projectDetailParams } from 'api';
import { useSearch } from 'contexts';
import { useMounted, useQuery } from 'hooks';

type ProjectContext = {
	project: any;
	setProject: any;
	branches: any[];
	params: any;
	setParams: any;
	isOwner: boolean;
	role: string;
	capabilities: any;
	rootApi: string;
	rootLink: string;
	loading: boolean;
	branchesLoading: boolean;
	onUpdate: any;
	onRefresh: any;
	fetchBranches: any;
	deleteBranch: any;
	switchBranch: any;
};

export const ProjectContext = React.createContext<ProjectContext>(null);

const ProjectProvider = ({ ssr, workspace, router, session, store, toast, children }) => {
	const [project, setProject] = useState(ssr.project?.data || {});
	const [branches, setBranches] = useState(ssr.branches?.data || []);
	const [params, setParams] = useState({ page: 0, pageSize: '*', query: '' });
	const search = useSearch();

	// Computed
	const rootApi = `${workspace.rootApi}/projects/${project.uuid}`;
	const rootLink = `/projects/${project.uuid}`;
	const role = project.access?.role || null;
	const capabilities = project.access?.capabilities || {};
	const isOwner = role
		? Boolean(capabilities.canDeleteProject)
		: session.user?.id === project.projectOwnerId ||
			(workspace.workspace?.id === project.workspaceId && workspace.workspace?.memberRole === 'owner');

	// Hooks
	useEffect(() => {
		if (ssr.project?.data) setProject(ssr.project.data);
		if (ssr.branches?.data) setBranches(ssr.branches.data);
	}, [ssr.project, ssr.branches]);

	useMounted(() => {
		if (project.id) fetchData();
	}, [router.query.branch]);

	// Actions
	const [fetchData, loading] = useQuery(async () => {
		const response = await store.queryRecord({
			url: rootApi,
			params: { branch: router.query.branch, ...projectDetailParams },
		});
		setProject(response.data);
	});

	const [fetchBranches, branchesLoading] = useQuery(async () => {
		const response = await store.query({ url: `${rootApi}/branches`, params });
		setBranches(response.data);
		return response.data;
	});

	const [switchBranch] = useQuery(async branch => {
		const response = await store.createRecord({ url: `${rootApi}/branches/${branch.id}/switch` });
		setProject(current => ({ ...current, branch: response.data }));
		setBranches(current => current.map(value => (value.id === response.data.id ? response.data : value)));
		search.onFilter({ branch: '' });
		return response.data;
	});

	// Methods
	const onUpdate = data => setProject({ ...project, ...data });

	const onRefresh = () => fetchData();

	const deleteBranch = branch => {
		const nextBranches = branches.filter(value => value.id !== branch.id);
		const defaultBranch = nextBranches.find(value => value.default);
		if (project.branch?.id === branch.id && defaultBranch) switchBranch(defaultBranch);
		setBranches(nextBranches);
	};

	// Render
	return (
		<ProjectContext.Provider
			value={{
				project,
				setProject,
				branches,
				params,
				setParams,
				isOwner,
				role,
				capabilities,
				rootApi,
				rootLink,
				loading,
				branchesLoading,
				onUpdate,
				onRefresh,
				fetchBranches,
				deleteBranch,
				switchBranch,
			}}>
			{children}
		</ProjectContext.Provider>
	);
};

const useProject = () => {
	return React.useContext(ProjectContext);
};

export { ProjectProvider, useProject };
